import {
    MutableRefObject,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useImageBuilder } from '../../utils/image';
import { useWindowSize } from '@uidotdev/usehooks';

export type ImageEditorRef = {
    rotate: (rotation: number) => void;
};

type ImageEditorProps = {
    src?: string;
    editorRef?: MutableRefObject<ImageEditorRef | undefined>;
};

const Rotation = {
    None: 0,
    Right: 90,
    Flip: 180,
    Left: 270,
};

function correctRotation(rotation: number) {
    return !rotation
        ? Rotation.None
        : rotation < Rotation.Flip
          ? Rotation.Right
          : rotation < Rotation.Left
            ? Rotation.Flip
            : Rotation.Left;
}

export default function ImageEditor({ src, editorRef }: ImageEditorProps) {
    const image = useRef<HTMLImageElement>(new Image());
    const canvas = useRef<HTMLCanvasElement>();
    const { loadImage } = useImageBuilder();
    const { width, height } = useWindowSize();

    const [rotation, setRotation] = useState(0);

    const update = () => {
        const ctx = canvas.current?.getContext('2d');
        if (ctx) {
            ctx.canvas.setAttribute('src', image.current.src);

            const rotate = correctRotation(rotation);
            const hRatio = (canvas.current?.width ?? 0) / image.current.width;
            const vRatio = (canvas.current?.height ?? 0) / image.current.height;
            const ratio = Math.min(hRatio, vRatio);
            const centerShift_x =
                ((canvas.current?.width ?? 0) - image.current.width * ratio) /
                2;
            const centerShift_y =
                ((canvas.current?.height ?? 0) - image.current.height * ratio) /
                2;

            let offsetX = 0;
            let offsetY = 0;

            switch (rotate) {
                case Rotation.Flip:
                    offsetX = canvas.current?.width ?? 0;
                    offsetY = canvas.current?.height ?? 0;
                    break;

                case Rotation.Left:
                    offsetY = canvas.current?.height ?? 0;
                    break;

                case Rotation.Right:
                    offsetX = canvas.current?.width ?? 0;
                    break;
            }

            ctx.save();
            ctx.clearRect(0, 0, width ?? 0, height ?? 0);
            ctx.setTransform(1, 0, 0, 1, offsetX, offsetY);
            ctx.rotate((rotate * Math.PI) / 180);
            ctx.drawImage(
                image.current,
                0,
                0,
                image.current.width,
                image.current.height,
                centerShift_x,
                centerShift_y,
                image.current.width * ratio,
                image.current.height * ratio,
            );
            ctx.restore();
        }
    };

    const rotate = useCallback(
        (degrees: number) => {
            if (degrees < 0) {
                if (rotation - degrees * -1 < 0) {
                    degrees = 270;
                } else {
                    degrees = rotation - degrees * -1;
                }
            } else {
                if (rotation + degrees >= 360) {
                    degrees = 0;
                } else {
                    degrees = rotation + degrees;
                }
            }
            setRotation(degrees);
        },
        [rotation],
    );

    useEffect(() => {
        if (editorRef) {
            editorRef.current = {
                rotate,
            };
        }
    }, [editorRef, rotate]);

    useEffect(() => {
        if (src && canvas.current?.getAttribute('src') !== src) {
            let blobUrl: string;
            loadImage(src).then(blob => {
                image.current.onload = update;
                blobUrl = URL.createObjectURL(blob);
                image.current.src = blobUrl;
            });

            return () => {
                if (blobUrl) {
                    URL.revokeObjectURL(blobUrl);
                }
            };
        }
    }, [src, canvas.current]);

    useLayoutEffect(update, [width, height, canvas, rotation]);

    return useMemo(() => {
        if (!width || !height) {
            return null;
        }

        return (
            <canvas
                ref={ref => {
                    if (ref) {
                        canvas.current = ref;
                    }
                }}
                height={height - 100}
                width={width}
            />
        );
    }, [width, height]);
}
